import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DataClass } from 'scrivito'

import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import InfoBox from '@/Atomic/Molecules/InfoBox'
import { CustomField } from '@/Atomic/Organisms/CustomField'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { Form, FormBody, FormFields, FormGroup } from '@/Atomic/Templates/Form'
import { errorToast } from '@/Components/Toast'
import { useDataClassFormHandler } from '@/utils/CRUD/hooks/useDataClassFormHandler'
import { getDataClassFormMap } from '@/utils/CRUD/read/dataClass'

interface DataClassCompanyBoundDrawerProps {
  dataClass: DataClass
  companyId?: string
  companyName: string | null
  isOpen: boolean
  close: () => void
  onCreated?: (itemId: string) => void
  createTitleKey: string
  linkedToKey: string
  relationField?: string
}

export const DataClassCompanyBoundDrawer: React.FC<
  DataClassCompanyBoundDrawerProps
> = ({
  dataClass,
  companyId,
  companyName,
  isOpen,
  close,
  onCreated,
  createTitleKey,
  linkedToKey,
  relationField,
}) => {
  const { t } = useTranslation()

  const { methods } = useDataClassFormHandler(dataClass, {
    prefill: { enabled: isOpen },
  })
  const { getValues, watch, reset, handleSubmit } = methods

  const groups = getDataClassFormMap(dataClass, getValues, watch).map(
    (group) => ({
      ...group,
      fields: relationField
        ? group.fields.filter((field) => field.name !== relationField)
        : group.fields,
    }),
  )

  const onDrawerClose = () => {
    reset()
    close()
  }

  const handleCreate = handleSubmit(async (data) => {
    try {
      const dataItem = await dataClass.create({
        ...data,
        ...(relationField && companyId ? { [relationField]: companyId } : {}),
      })
      reset()
      close()
      onCreated?.(dataItem.id())
    } catch (error) {
      errorToast(
        error instanceof Error
          ? error
          : new Error(t('errors.unknownError.headline')),
      )
    }
  })

  return (
    <Drawer isOpen={isOpen} close={onDrawerClose} direction="right">
      <DrawerHeader>
        {t(createTitleKey)}
        <Button
          variant="icon"
          icon="cross"
          tooltip={t('tooltip.close')}
          onClick={onDrawerClose}
        />
      </DrawerHeader>
      <DrawerBody>
        <FormProvider {...methods}>
          <Form onSubmit={handleCreate}>
            <FormBody>
              {companyName && (
                <InfoBox
                  iconPosition="side"
                  text={t(linkedToKey, {
                    contactName: companyName,
                  })}
                />
              )}
              {groups.map((group) => (
                <FormGroup key={group.name}>
                  <StringText variant="title">{group.title}</StringText>
                  <FormFields>
                    {group.fields.map((field) => (
                      <CustomField
                        key={field.fieldKey}
                        fieldMap={field}
                        isEditing={true}
                        className="form-field"
                      />
                    ))}
                  </FormFields>
                </FormGroup>
              ))}
            </FormBody>
          </Form>
        </FormProvider>
      </DrawerBody>
      <DrawerFooter align="right">
        <Button variant="outlined" onClick={onDrawerClose} size="lg">
          {t('common.cancel')}
        </Button>
        <Button icon="disk" onClick={handleCreate} size="lg">
          {t('common.save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  )
}
